// Visão geral: um cartão por regulador com o estado da máquina de relance — tensão,
// potências e frequência em mostradores, e o carregamento (corrente, potência aparente,
// campo) em % do nominal. Cada cartão leva ao painel completo do regulador.
//
// Uma chamada só (/api/dashboard) para todos os reguladores; o tempo real invalida a
// consulta nas mudanças de link e alarme, e o intervalo curto acompanha as grandezas.

import { useQuery } from "@tanstack/react-query";
import { Activity, AlertTriangle, ChevronRight, CircleOff, Clock } from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router";
import { Bars } from "../components/charts/Bars";
import { NominalNotice, pctOf, readingMap, TagGauge } from "../components/charts/Readings";
import { Badge, Card, EmptyState, PageHeader, Spinner, cx } from "../components/ui";
import { api, type DashboardRegulator, type UnitPrefs } from "../lib/api";
import { HOME_GAUGES, HOME_LOAD } from "../lib/gauges";
import { useMe } from "../lib/session";
import { displayFor, formatValue } from "../lib/units";

export function HomePage() {
  const { data: me } = useMe();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api.get<{ regulators: DashboardRegulator[] }>("/api/dashboard"),
    refetchInterval: 5_000,
    placeholderData: (prev) => prev,
  });
  const regs = data?.regulators ?? [];
  const online = regs.filter((r) => r.online).length;
  const alarms = regs.reduce((n, r) => n + r.activeAlarms, 0);

  // Agrupado por usina, na ordem que a API manda (grupo, usina, UG, regulador).
  const sites = useMemo(() => {
    const out: { key: string; title: string; regs: DashboardRegulator[] }[] = [];
    const multiGroup = new Set(regs.map((r) => r.group.id)).size > 1;
    for (const r of regs) {
      const key = `${r.site.id}`;
      let s = out.find((x) => x.key === key);
      if (!s) out.push((s = { key, title: multiGroup ? `${r.group.name} · ${r.site.name}` : r.site.name, regs: [] }));
      s.regs.push(r);
    }
    return out;
  }, [regs]);

  return (
    <>
      <PageHeader title={`Olá, ${me?.user.name.split(" ")[0] ?? ""}`} description="Usinas e reguladores que você acompanha." />

      {isLoading ? (
        <Spinner />
      ) : regs.length === 0 ? (
        <EmptyState title="Nenhum regulador visível">
          Seu acesso ainda não foi vinculado a nenhuma usina com reguladores. Fale com o gestor da sua planta.
        </EmptyState>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Reguladores" value={regs.length} />
            <Stat label="Com link ativo" value={online} />
            <Stat label="Sem link" value={regs.length - online} tone={regs.length - online ? "warn" : undefined} />
            <Stat label="Alarmes ativos" value={alarms} tone={alarms ? "danger" : undefined} to="/alarmes" />
          </div>

          <div className="space-y-8">
            {sites.map((s) => (
              <section key={s.key}>
                <h2 className="mb-3 text-sm font-medium text-muted">{s.title}</h2>
                <div className="grid gap-4 xl:grid-cols-2">
                  {s.regs.map((r) => (
                    <RegulatorCard key={r.id} reg={r} prefs={me?.unitPrefs} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </>
      )}
    </>
  );
}

function Stat({ label, value, tone, to }: { label: string; value: number; tone?: "warn" | "danger"; to?: string }) {
  const body = (
    <Card className={cx("p-4", to && "transition-shadow hover:shadow-md")}>
      <p className="text-xs text-muted">{label}</p>
      <p className={cx("mt-1 text-2xl font-semibold", tone === "warn" && "text-warn", tone === "danger" && "text-danger")}>{value}</p>
    </Card>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

function RegulatorCard({ reg: r, prefs }: { reg: DashboardRegulator; prefs: UnitPrefs | undefined }) {
  const readings = useMemo(() => readingMap(r.analog), [r.analog]);
  const stale = !r.online;
  const load = useMemo(
    () =>
      HOME_LOAD.filter((l) => readings.has(l.tag) && r.nominal[l.nominal]).map((l) => {
        const a = readings.get(l.tag)!;
        const d = displayFor(a.base, a.unit, prefs);
        const pct = pctOf(a.value, r.nominal[l.nominal]?.value);
        return { label: l.label, value: pct, text: `${formatValue(a.value, d)} ${d.unit}` };
      }),
    [readings, r.nominal, prefs],
  );
  // Escala fixa até 150 %: uma barra fora da faixa satura (o rótulo diz o valor) em vez de
  // achatar as outras.
  const loadMax = 150;

  return (
    <Card className="flex flex-col">
      <Link to={`/reguladores/${r.id}`} className="group flex flex-wrap items-center gap-2 border-b border-line px-4 py-3 hover:bg-surface-2">
        <div className="min-w-0">
          <p className="font-semibold leading-tight">{r.name}</p>
          <p className="text-xs text-muted">
            {r.unit.name} · {r.model === "vtv" ? "GRTD-2000 VTV" : "GRTD-2000"}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {r.activeAlarms > 0 && (
            <Badge tone="danger">
              <AlertTriangle className="size-3" aria-hidden /> {r.activeAlarms} alarme{r.activeAlarms > 1 ? "s" : ""}
            </Badge>
          )}
          {r.online ? (
            <Badge tone="ok">
              <Activity className="size-3" aria-hidden /> online
            </Badge>
          ) : (
            <Badge tone={r.online === false ? "warn" : "neutral"}>
              <CircleOff className="size-3" aria-hidden /> {r.online === false ? "sem link" : "sem dados"}
            </Badge>
          )}
          <ChevronRight className="size-4 text-muted transition-transform group-hover:translate-x-0.5" aria-hidden />
        </div>
      </Link>

      {r.analog.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-muted">Nenhuma leitura recebida ainda.</p>
      ) : (
        <div className="space-y-3 px-4 pt-3 pb-4">
          <div className="grid grid-cols-2 gap-x-2 gap-y-1 sm:grid-cols-4">
            {HOME_GAUGES.map((tag) => (
              <TagGauge key={tag} tag={tag} readings={readings} nominal={r.nominal} prefs={prefs} stale={stale} height={130} />
            ))}
          </div>
          {load.length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted">Carregamento (% do nominal · tracejado = 100 %)</p>
              <Bars title="Carregamento em % do nominal" items={load.map((l) => ({ ...l, text: l.value === null ? "—" : `${Math.round(l.value)}% · ${l.text}` }))} max={loadMax} mark={100} unit="%" stale={stale} />
            </div>
          )}
          <NominalNotice nominal={r.nominal} />
        </div>
      )}

      <p className="mt-auto flex items-center gap-1.5 border-t border-line px-4 py-2 text-xs text-muted">
        <Clock className="size-3.5" aria-hidden />
        último dado {r.lastSeen ? new Date(r.lastSeen).toLocaleString("pt-BR") : "—"}
        {!r.online && r.linkDetail && <span className="ml-1 truncate text-warn">· {r.linkDetail}</span>}
      </p>
    </Card>
  );
}
